import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, File, X } from 'lucide-react';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { SUPPORTED_FILE_TYPES, MAX_FILE_SIZE } from '../../../constants/api';
import './FileUpload.css';

interface FileUploadProps {
  onFileSelect: (file: File) => void;
  loading?: boolean;
  error?: string;
  progress?: number;
}

const FileUpload: React.FC<FileUploadProps> = ({
  onFileSelect,
  loading = false,
  error,
  progress,
}) => {
  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      onFileSelect(acceptedFiles[0]);
    }
  }, [onFileSelect]);

  const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
    },
    maxSize: MAX_FILE_SIZE,
    multiple: false,
  });

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="file-upload-container">
      <div
        {...getRootProps()}
        className={`file-upload-dropzone ${isDragActive ? 'active' : ''} ${loading ? 'loading' : ''}`}
      >
        <input {...getInputProps()} />
        
        <div className="upload-content">
          <Upload size={48} className="upload-icon" />
          <h3>Upload Contract Document</h3>
          <p>
            {isDragActive
              ? 'Drop the file here...'
              : 'Drag & drop a contract file here, or click to select'}
          </p>
          <p className="file-info">
            Supports PDF, JPG, PNG up to {formatFileSize(MAX_FILE_SIZE)}
          </p>
        </div>
        
        {loading && progress !== undefined && (
          <div className="upload-progress">
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${progress}%` }}
              />
            </div>
            <span className="progress-text">{progress}%</span>
          </div>
        )}
      </div>
      
      {error && (
        <div className="upload-error">
          <X size={16} />
          <span>{error}</span>
        </div>
      )}
      
      {fileRejections.length > 0 && (
        <div className="upload-error">
          <X size={16} />
          <span>
            File rejected: {fileRejections[0].errors[0]?.message || 'Invalid file'}
          </span>
        </div>
      )}
    </div>
  );
};

export default FileUpload;